import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import { switchMap, tap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { enumValues, isPresent } from '@fcom/core/utils';
import {
  createDeeplinkPathFromFlightSearchParams,
  mapOndMarketingOfferToFlightSearchParams,
} from '@fcom/common/utils/flight-search.utils';

import { OndMarketingOffer, OndPairEnhancedResponse } from '../../interfaces';
import { OndMarketingOffersService } from '../../services';

const INITIAL_OFFER_AMOUNT = 4;
const OFFERS_TO_LOAD_AMOUNT = 3;

interface SortOption {
  value: string;
  label: string;
}

export enum OfferListSortBy {
  DEPARTURE = 'departureDate',
  PRICE_ASC = 'priceAscending',
  PRICE_DESC = 'priceDescending',
}

@Component({
  selector: 'fin-ond-marketing-offers-list',
  templateUrl: './ond-marketing-offers-list.component.html',
  styleUrls: ['./ond-marketing-offers-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OndMarketingOffersListComponent {
  private ondMarketingOffersService = inject(OndMarketingOffersService);
  private languageService = inject(LanguageService);
  private router = inject(Router);

  readonly SvgLibraryIcon = SvgLibraryIcon;

  originLocationCode = input<string | undefined>(undefined);
  destinationLocationCode = input<string | undefined>(undefined);
  links = input<OndPairEnhancedResponse>();

  readonly defaultSort: OfferListSortBy = OfferListSortBy.PRICE_ASC;
  readonly moreOffersAmount: number = OFFERS_TO_LOAD_AMOUNT;

  offerAmount = signal(INITIAL_OFFER_AMOUNT);
  dynamicAnimationDisabled = signal(true);
  sortBy = signal<OfferListSortBy>(this.defaultSort);

  private readonly sortByValues: string[] = enumValues(OfferListSortBy);

  private readonly notSortedOffers = signal<OndMarketingOffer[]>([]);
  readonly heading = signal('');

  readonly ondOffers = computed(() => {
    const sortBy = this.sortBy();
    return [...this.notSortedOffers()].sort((a, b) => {
      switch (sortBy) {
        case OfferListSortBy.PRICE_ASC:
          return parseFloat(a.price.amount) > parseFloat(b.price.amount) ? 1 : -1;
        case OfferListSortBy.PRICE_DESC:
          return parseFloat(a.price.amount) > parseFloat(b.price.amount) ? -1 : 1;
        case OfferListSortBy.DEPARTURE:
          return a.departureDate > b.departureDate ? 1 : -1;
        default:
          return 0;
      }
    });
  });

  private readonly sortOptionLabels = this.languageService.translateMultipleSignal(
    this.sortByValues.map((key) => `ondMarketing.sortOfferOptions.${key}`)
  );

  readonly sortOptions = computed<SortOption[]>(() => {
    const labels = this.sortOptionLabels();
    if (!labels) {
      return [];
    }
    return labels
      .map((label: string, i: number) => ({ value: this.sortByValues[i] as OfferListSortBy, label }))
      .filter((option: SortOption) => isPresent(option.label));
  });

  constructor() {
    effect((onCleanup) => {
      const origin = this.originLocationCode();
      const destination = this.destinationLocationCode();
      const links = this.links();

      if (!destination && !links) {
        return;
      }

      const subscription = this.ondMarketingOffersService
        .getOffersOrFlightData(origin, destination, links)
        .pipe(
          tap((offers) => this.notSortedOffers.set(offers)),
          switchMap((offers) =>
            this.ondMarketingOffersService.getHeading(origin, destination, offers, 'mostSearchedDeals')
          )
        )
        .subscribe((heading) => this.heading.set(heading));

      onCleanup(() => subscription.unsubscribe());
    });
  }

  loadMoreOffers(): void {
    this.dynamicAnimationDisabled.set(false);
    this.offerAmount.update((amount) => amount + this.moreOffersAmount);
  }

  sortOffers(value: OfferListSortBy): void {
    this.sortBy.set(value);
  }

  navigateToUrl(ondOffer: OndMarketingOffer): void {
    const flightSearchParams = mapOndMarketingOfferToFlightSearchParams(ondOffer, this.languageService.langValue);

    if (flightSearchParams) {
      const deeplink = createDeeplinkPathFromFlightSearchParams(flightSearchParams);
      this.router.navigateByUrl(deeplink);
    }
  }
}
